<script lang="ts" setup>
import ApplicationLogo from "@/components/ApplicationLogo.vue";
import NotificationList from "@/components/NotificationList.vue";
import SidebarProvider from "@/components/ui/sidebar/SidebarProvider.vue";
import SidebarContent from "@/components/ui/sidebar/SidebarContent.vue";
import SidebarMenu from "@/components/ui/sidebar/SidebarMenu.vue";
import SearchTrigger from "@/components/ui/search/SearchTrigger.vue";
import DarkmodeButton from "@/components/DarkmodeButton.vue";
import { ChevronDown, LogOut, Settings, User } from "lucide-vue-next";
import { Link } from "@inertiajs/vue3";

function url(str: string) {
  return new URL(str);
}
</script>

<template>
  <div class="relative isolate flex min-h-svh max-lg:flex-col">
    <Head :title="$page.props.title" />

    <NotificationList />

    <SearchProvider>
      <SearchContent />
      <SidebarProvider>
        <Sidebar collapsible="icon">
          <SidebarHeader class="p-0 flex items-center w-full px-4">
            <div class="border-b h-12 flex items-center w-full shrink-0 overflow-hidden">
              <ApplicationLogo class="h-10 w-auto shrink-0" />
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SearchTrigger />
            </SidebarGroup>
            <SidebarGroup v-for="group in (($page.props?.menu as any) ?? []).filter(p => p.children?.length > 0)">
              <SidebarGroupLabel class="text-gray-500">{{ group.title }}</SidebarGroupLabel>
              <SidebarMenu>
                <SidebarMenuItem
                  v-for="item in group.children"
                  :key="item.title"
                >
                  <SidebarMenuButton
                    :tooltip="item.title"
                    :as="Link"
                    prefetch="hover"
                    :href="item.href"
                    :is-active="url(item.href).pathname == $page.url"
                  >
                    <Icon :name="item.icon" />
                    <span>{{ item.title }}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>
          <SidebarRail />
        </Sidebar>
        <SidebarInset>
          <header class="flex w-full0 justify-between h-12 px-4 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <div class="border-b flex items-center justify-between h-12 w-full">
              <div class="flex items-center gap-2">
                <SidebarTrigger class="" />
                <Separator
                  orientation="vertical"
                  class="mr-2 h-4"
                />
                <Breadcrumb>
                  <BreadcrumbList>
                    <template v-for="(item, index) in $page.props.breadcrumbs">
                      <BreadcrumbItem class="hidden md:block">
                        <BreadcrumbLink
                          :href="item.href"
                          :as="Link"
                        >
                          {{ item.title }}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator
                        v-if="index != $page.props.breadcrumbs.length - 1"
                        class="hidden md:block"
                      />
                    </template>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <div class="flex items-center gap-4">
                <NotificationButton />
                <DarkmodeButton />
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <Button
                      variant="ghost"
                      class="h-auto px-1 hover:bg-transparent"
                    >
                      <Avatar
                        size="sm"
                        class="rounded-full"
                      >
                        <AvatarFallback>
                          {{
                            $page.props?.auth?.user?.name
                              .split(" ")
                              .map((s) => s.substring(0, 1).toUpperCase())
                              .join("")
                          }}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown
                        :size="16"
                        :strokeWidth="2"
                        className="ms-2 opacity-60"
                        aria-hidden="true"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent class="w-56">
                    <DropdownMenuGroup>
                      <div class="px-2 py-1.5 block text-sm space-y-px">
                        <p class="truncate">{{ $page.props.auth.user.name }}</p>
                        <p class="truncate">{{ $page.props.auth.user.email }}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        :as="Link"
                        :href="route('profile.edit')"
                      >
                        <User class="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        :as="Link"
                        :href="route('settings.edit')"
                      >
                        <Settings class="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      :as="Link"
                      class="w-full"
                      method="post"
                      :href="route('logout')"
                    >
                      <LogOut class="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>
          <div class="flex flex-1 flex-col">
            <div class="p-4">
              <slot></slot>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </SearchProvider>
  </div>
</template>
